import React, { useState, useEffect } from 'react';
import { formatPhoneNumberInput } from '../../utils/profileUtils';
import PrimaryButton from '../../../website/components/account-setup/shared/PrimaryButton';
import { useAccountSettingsStyleVars } from '../../../shared/hooks/useAccountSettingsStyleVars';
import '../../../components/settings/pages/AccountSettings/AccountSettings.css';
import '../../../shared/styles/accountFieldsFlat.css';

const emptyForm = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  address: {
    street1: '',
    street2: '',
    city: '',
    state: '',
    zipCode: '',
  },
  emergencyContact: {
    name: '',
    phone: '',
    relationship: '',
  },
};

const clientToForm = (client) => ({
  firstName: client?.firstName || '',
  lastName: client?.lastName || '',
  email: client?.email || '',
  phone: client?.phone ? formatPhoneNumberInput(client.phone) : '',
  address: {
    street1: client?.address?.street1 || '',
    street2: client?.address?.street2 || '',
    city: client?.address?.city || '',
    state: client?.address?.state || '',
    zipCode: client?.address?.zipCode || '',
  },
  emergencyContact: {
    name: client?.emergencyContact?.name || '',
    phone: client?.emergencyContact?.phone
      ? formatPhoneNumberInput(client.emergencyContact.phone)
      : '',
    relationship: client?.emergencyContact?.relationship || '',
  },
});

/**
 * Client profile editor — field styling matches Account Settings (asCard / asInput).
 */
const ClientProfileEditor = ({ client, onSave, saving = false }) => {
  const cssVars = useAccountSettingsStyleVars();
  const [formData, setFormData] = useState(emptyForm);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (client) setFormData(clientToForm(client));
  }, [client?.id, client?.updatedAt, client]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      setFormData((prev) => ({ ...prev, phone: formatPhoneNumberInput(value) }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      address: { ...prev.address, [name]: value },
    }));
  };

  const handleEmergencyChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      setFormData((prev) => ({
        ...prev,
        emergencyContact: {
          ...prev.emergencyContact,
          phone: formatPhoneNumberInput(value),
        },
      }));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      emergencyContact: { ...prev.emergencyContact, [name]: value },
    }));
  };

  const handleReset = () => {
    setError(null);
    if (client) setFormData(clientToForm(client));
  };

  const handleSubmit = async () => {
    if (!formData.firstName?.trim() || !formData.lastName?.trim()) {
      setError('First name and last name are required.');
      return;
    }
    setError(null);
    const payload = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      email: formData.email,
      phone: formData.phone,
      searchName: `${formData.firstName} ${formData.lastName}`.trim().toLowerCase(),
      address: {
        street1: formData.address.street1,
        street2: formData.address.street2,
        city: formData.address.city,
        state: formData.address.state,
        zipCode: formData.address.zipCode,
      },
      emergencyContact: {
        name: formData.emergencyContact.name,
        phone: formData.emergencyContact.phone,
        relationship: formData.emergencyContact.relationship,
      },
    };
    await onSave(payload);
  };

  if (!client) return null;

  return (
    <div className="accountFieldsFlat" style={cssVars}>
      {error ? <p className="asErrorText">{error}</p> : null}

      <section className="asCard" aria-label="Personal information">
        <div className="asCardHeader">
          <h2>Personal information</h2>
          <p className="sub">Name and contact details.</p>
        </div>
        <div className="asCardBody">
          <div className="asFormGrid">
            <div className="asField">
              <div className="asFieldLabelRow">
                <label className="asLabel" htmlFor="client-firstName">
                  First name
                </label>
              </div>
              <input
                id="client-firstName"
                className="asInput"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                autoComplete="given-name"
                disabled={saving}
              />
            </div>
            <div className="asField">
              <div className="asFieldLabelRow">
                <label className="asLabel" htmlFor="client-lastName">
                  Last name
                </label>
              </div>
              <input
                id="client-lastName"
                className="asInput"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                autoComplete="family-name"
                disabled={saving}
              />
            </div>
            <div className="asField">
              <div className="asFieldLabelRow">
                <label className="asLabel" htmlFor="client-email">
                  Email
                </label>
              </div>
              <input
                id="client-email"
                className="asInput"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                autoComplete="email"
                disabled={saving}
              />
            </div>
            <div className="asField">
              <div className="asFieldLabelRow">
                <label className="asLabel" htmlFor="client-phone">
                  Phone
                </label>
              </div>
              <input
                id="client-phone"
                className="asInput"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="(123) 456-7890"
                maxLength={14}
                disabled={saving}
              />
              <p className="asHelperText">Format: (123) 456-7890</p>
            </div>
          </div>
        </div>
      </section>

      <section className="asCard" aria-label="Address">
        <div className="asCardHeader">
          <h2>Address</h2>
          <p className="sub">Service location and mailing details.</p>
        </div>
        <div className="asCardBody">
          <div className="asFormGrid asFormGrid--three">
            <div className="asField" style={{ gridColumn: '1 / -1' }}>
              <div className="asFieldLabelRow">
                <label className="asLabel" htmlFor="client-street1">
                  Street address
                </label>
              </div>
              <input
                id="client-street1"
                className="asInput"
                name="street1"
                value={formData.address.street1}
                onChange={handleAddressChange}
                disabled={saving}
              />
            </div>
            <div className="asField" style={{ gridColumn: '1 / -1' }}>
              <div className="asFieldLabelRow">
                <label className="asLabel" htmlFor="client-street2">
                  Apt, suite (optional)
                </label>
              </div>
              <input
                id="client-street2"
                className="asInput"
                name="street2"
                value={formData.address.street2}
                onChange={handleAddressChange}
                disabled={saving}
              />
            </div>
            <div className="asField">
              <div className="asFieldLabelRow">
                <label className="asLabel" htmlFor="client-city">
                  City
                </label>
              </div>
              <input
                id="client-city"
                className="asInput"
                name="city"
                value={formData.address.city}
                onChange={handleAddressChange}
                disabled={saving}
              />
            </div>
            <div className="asField">
              <div className="asFieldLabelRow">
                <label className="asLabel" htmlFor="client-state">
                  State
                </label>
              </div>
              <input
                id="client-state"
                className="asInput"
                name="state"
                value={formData.address.state}
                onChange={handleAddressChange}
                disabled={saving}
              />
            </div>
            <div className="asField">
              <div className="asFieldLabelRow">
                <label className="asLabel" htmlFor="client-zip">
                  ZIP code
                </label>
              </div>
              <input
                id="client-zip"
                className="asInput"
                name="zipCode"
                value={formData.address.zipCode}
                onChange={handleAddressChange}
                disabled={saving}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="asCard" aria-label="Emergency contact">
        <div className="asCardHeader">
          <h2>Emergency contact</h2>
          <p className="sub">Someone we can reach if needed.</p>
        </div>
        <div className="asCardBody">
          <div className="asFormGrid">
            <div className="asField">
              <div className="asFieldLabelRow">
                <label className="asLabel" htmlFor="ec-name">
                  Name
                </label>
              </div>
              <input
                id="ec-name"
                className="asInput"
                name="name"
                value={formData.emergencyContact.name}
                onChange={handleEmergencyChange}
                disabled={saving}
              />
            </div>
            <div className="asField">
              <div className="asFieldLabelRow">
                <label className="asLabel" htmlFor="ec-phone">
                  Phone
                </label>
              </div>
              <input
                id="ec-phone"
                className="asInput"
                name="phone"
                value={formData.emergencyContact.phone}
                onChange={handleEmergencyChange}
                placeholder="(123) 456-7890"
                maxLength={14}
                disabled={saving}
              />
            </div>
            <div className="asField" style={{ gridColumn: '1 / -1' }}>
              <div className="asFieldLabelRow">
                <label className="asLabel" htmlFor="ec-relationship">
                  Relationship
                </label>
              </div>
              <input
                id="ec-relationship"
                className="asInput"
                name="relationship"
                value={formData.emergencyContact.relationship}
                onChange={handleEmergencyChange}
                disabled={saving}
              />
            </div>
          </div>
        </div>
      </section>

      <div className="asActions">
        <span className="asSavingNote">{saving ? 'Saving changes…' : ''}</span>
        <button type="button" className="asBtnSecondary" onClick={handleReset} disabled={saving}>
          Reset
        </button>
        <PrimaryButton
          onClick={handleSubmit}
          disabled={saving || !formData.firstName?.trim() || !formData.lastName?.trim()}
          style={{ width: 220, pointerEvents: saving ? 'none' : 'auto' }}
        >
          {saving ? 'Saving…' : 'Save profile'}
        </PrimaryButton>
      </div>
    </div>
  );
};

export default ClientProfileEditor;
